import {
  COLUMN_RESIZE,
  DEFAULT_COLUMN_WIDTH,
  type ColumnSizingState,
  type IColumnDefMeta,
} from "@ryde/shared";

const clampColumnWidth = (width: number): number =>
  Math.max(COLUMN_RESIZE.minWidth, Math.round(width));

/** Truncated, not rounded: the frozen widths must never add up to more than the table had, or it starts to scroll. */
export const truncateMeasuredWidth = (width: number): number =>
  Math.floor(width * COLUMN_RESIZE.measuredWidthPrecision) /
  COLUMN_RESIZE.measuredWidthPrecision;

/** Fixed columns (row marker, action icons, id) and the trailing settings column keep their width. */
export const isColumnResizable = (meta?: IColumnDefMeta): boolean =>
  !meta?.fixed && !meta?.last;

export const getPointerResizedColumnWidth = ({
  startWidth,
  startClientX,
  clientX,
  isRtl,
}: {
  startWidth: number;
  startClientX: number;
  clientX: number;
  isRtl: boolean;
}): number => {
  // The handle sits on the column's end edge, which is its left edge in RTL: moving left widens it there.
  const direction = isRtl ? -1 : 1;

  return clampColumnWidth(startWidth + (clientX - startClientX) * direction);
};

/** Arrow keys move the handle like the pointer does; any other key is not a resize. */
export const getKeyboardResizedColumnWidth = ({
  width,
  key,
  isRtl,
}: {
  width: number;
  key: string;
  isRtl: boolean;
}): number | undefined => {
  const widenKey = isRtl ? "ArrowLeft" : "ArrowRight";
  const narrowKey = isRtl ? "ArrowRight" : "ArrowLeft";

  if (key === widenKey) {
    return clampColumnWidth(width + COLUMN_RESIZE.keyboardStep);
  }

  if (key === narrowKey) {
    return clampColumnWidth(width - COLUMN_RESIZE.keyboardStep);
  }

  return undefined;
};

/**
 * Width a header cell renders with. Once any column was resized the table stops stretching, and a column
 * without a width of its own would collapse to nothing, so it gets the default width instead.
 */
export const resolveHeaderColumnWidth = ({
  sizedWidth,
  metaWidth,
  isTableSized,
}: {
  sizedWidth?: number;
  metaWidth?: string | number;
  isTableSized: boolean;
}): string | number | undefined =>
  sizedWidth ?? metaWidth ?? (isTableSized ? DEFAULT_COLUMN_WIDTH : undefined);

/** Distance from the top of `headerCell` to the bottom of its table's last row: the length of a full-column line. */
export const measureColumnLineHeight = (
  headerCell: HTMLTableCellElement,
): number => {
  const tableRows = headerCell.closest("table")?.rows;
  const lastRow = tableRows?.[tableRows.length - 1] ?? headerCell;

  return (
    lastRow.getBoundingClientRect().bottom -
    headerCell.getBoundingClientRect().top
  );
};

/** Current rendered width of every column in the header row of `headerCell`, keyed by column id. */
export const measureHeaderRowColumnsSizing = (
  headerCell: HTMLTableCellElement,
): ColumnSizingState => {
  const headerRowCells = Array.from(headerCell.parentElement?.children ?? []);

  return Object.fromEntries(
    headerRowCells.flatMap(cell => {
      const columnId =
        cell instanceof HTMLElement ? cell.dataset.columnId : undefined;

      return columnId
        ? [
            [
              columnId,
              truncateMeasuredWidth(cell.getBoundingClientRect().width),
            ],
          ]
        : [];
    }),
  );
};
